"use client";

import {
  Activity,
  AudioLines,
  Beaker,
  Check,
  Clock3,
  FlaskConical,
  Info,
  LoaderCircle,
  Mic,
  PackagePlus,
  Pill,
  RotateCcw,
  Search,
  Sparkles,
  Square,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { summarizeOrderDictation, type OrderDictationSummary } from "@/src/services/aiAgentService";
import { searchDrugs, type DrugEntry } from "@/src/services/drugCatalog";
import { useLang } from "@/src/i18n";
import {
  catalogItemToOrder,
  createManualOrder,
  parseOrderText,
  searchOrderCatalog,
  type AiAuditContext,
  type CatalogItem,
  type OrderCategory,
  type OrderLane,
  type ParsedOrder,
} from "@/src/services/mockAiService";

type OrderOmniboxProps = {
  onAddOrders: (orders: ParsedOrder[], audit?: AiAuditContext) => void;
};

type LaneChoice = "auto" | OrderLane;
type DictationState = "idle" | "recording" | "processing" | "review";
type DictationReview = OrderDictationSummary & { originalSummary: string };

const sample = "pct 500 1x3 po pc, CBC tomorrow, NSS 1000 80cc/hr continue";

const categoryIcon: Record<OrderCategory, typeof Pill> = {
  medication: Pill,
  lab: FlaskConical,
  procedure: Beaker,
  fluid: Activity,
  other: PackagePlus,
};

function orderSelection(orders: ParsedOrder[]) {
  return orders
    .map((order) => `${order.label} — ${order.details} → ${order.lane === "continuous" ? "Continuous" : "One-Day"}`)
    .join("; ");
}

export function OrderOmnibox({ onAddOrders }: OrderOmniboxProps) {
  const { t } = useLang();
  const [value, setValue] = useState("");
  const [parsing, setParsing] = useState(false);
  const [targetLane, setTargetLane] = useState<LaneChoice>("auto");
  const [dictation, setDictation] = useState<DictationState>("idle");
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [review, setReview] = useState<DictationReview | null>(null);
  const [showCatalog, setShowCatalog] = useState(false);

  const catalogMatches = value.trim() ? searchOrderCatalog(value) : [];
  const drugMatches = value.trim() ? searchDrugs(value) : [];
  // A "bundle" is multiple orders typed at once (comma / + / "then" / newline).
  const bundleSegments = value.trim()
    ? value.split(/\s*(?:,|;|\n|\r|\bthen\b|\band also\b|\+)\s*/i).map((s) => s.trim()).filter(Boolean)
    : [];
  const isBundle = bundleSegments.length > 1;

  function addDrug(drug: DrugEntry) {
    const lane = targetLane === "auto" ? drug.lane : targetLane;
    const order: ParsedOrder = {
      id: `tmt-${Date.now()}`,
      category: drug.category,
      label: drug.name,
      details: drug.direction,
      lane,
      stat: false,
      source: "catalog",
      tmtId: drug.tmtId,
      quantity: drug.quantity,
    };
    onAddOrders([order], {
      action: "Drug added from TMT formulary",
      userInput: value.trim() || drug.name,
      aiSuggestion: `${drug.name} (${drug.tmtId}) — ${drug.direction}`,
      selectedOutput: `${drug.name} → ${lane === "continuous" ? "Continuous" : "One-Day"}`,
      evidence: `Local TMT drug catalog · ${drug.tmtId} · clinician review required`,
      model: "TMT formulary (local)",
    });
    setValue("");
    setShowCatalog(false);
  }

  function addFromCatalog(item: CatalogItem) {
    const order = catalogItemToOrder(item, targetLane === "auto" ? undefined : targetLane);
    onAddOrders([order], {
      action: "Order added from catalog search",
      userInput: value.trim() || item.name,
      aiSuggestion: `${order.label} — ${order.details}`,
      selectedOutput: `${order.label} → ${order.lane === "continuous" ? "Continuous" : "One-Day"}`,
      evidence: "Hospital order catalog · clinician review required",
      model: "Nong SATI catalog",
    });
    setValue("");
    setShowCatalog(false);
  }

  useEffect(() => {
    if (dictation !== "recording") return;
    const timer = window.setInterval(() => setRecordingSeconds((seconds) => seconds + 1), 1000);
    return () => window.clearInterval(timer);
  }, [dictation]);

  async function submit() {
    if (!value.trim() || parsing) return;
    const doctorInput = value.trim();
    setParsing(true);
    const parsed = await parseOrderText(
      doctorInput,
      targetLane === "auto" ? undefined : targetLane,
    );
    onAddOrders(parsed, {
      action: "AI order parser confirmed",
      userInput: doctorInput,
      aiSuggestion: orderSelection(parsed),
      selectedOutput: orderSelection(parsed),
      evidence: "Hybrid Thai/English order parser · clinician review required",
      model: "Nong SATI order parser v2",
    });
    setValue("");
    setParsing(false);
  }

  function insertManual(category: OrderCategory) {
    onAddOrders([
      createManualOrder(
        category,
        targetLane === "continuous" ? "continuous" : "one-day",
      ),
    ]);
  }

  function startOrderDictation() {
    setReview(null);
    setRecordingSeconds(0);
    setDictation("recording");
  }

  async function stopAndSummarize() {
    setDictation("processing");
    const result = await summarizeOrderDictation(
      targetLane === "auto" ? undefined : targetLane,
    );
    setReview({ ...result, originalSummary: result.summary });
    setDictation("review");
  }

  async function confirmDictation() {
    if (!review?.summary.trim()) return;
    setParsing(true);
    const parsed = await parseOrderText(
      review.summary,
      targetLane === "auto" ? undefined : targetLane,
    );
    const confirmed = parsed.map((order) => ({ ...order, source: "dictation" as const }));
    onAddOrders(confirmed, {
      action: "Dictated orders summarized and confirmed",
      userInput: review.transcript,
      aiSuggestion: review.originalSummary,
      selectedOutput: review.summary,
      evidence: review.evidence,
      model: review.model,
    });
    setReview(null);
    setDictation("idle");
    setParsing(false);
  }

  const formattedRecording = `${String(Math.floor(recordingSeconds / 60)).padStart(2, "0")}:${String(recordingSeconds % 60).padStart(2, "0")}`;

  return (
    <div className="order-entry-tools">
      <div className="order-routing">
        <div>
          <span className="routing-label">{t("order.entry.placeIn")}</span>
          <div className="lane-selector" role="group" aria-label="Target order lane">
            <button
              type="button"
              className={targetLane === "auto" ? "active" : ""}
              aria-pressed={targetLane === "auto"}
              onClick={() => setTargetLane("auto")}
            >
              <Sparkles size={14} /> {t("order.entry.auto")}
            </button>
            <button
              type="button"
              className={targetLane === "one-day" ? "active" : ""}
              aria-pressed={targetLane === "one-day"}
              onClick={() => setTargetLane("one-day")}
            >
              <Clock3 size={14} /> {t("order.oneDay")}
            </button>
            <button
              type="button"
              className={targetLane === "continuous" ? "active" : ""}
              aria-pressed={targetLane === "continuous"}
              onClick={() => setTargetLane("continuous")}
            >
              <Activity size={14} /> {t("order.continuous")}
            </button>
          </div>
        </div>
        <p>
          <Info size={14} />
          {t("order.entry.routingHint")}
        </p>
      </div>

      {dictation === "recording" && (
        <div className="order-dictation-recording" role="status">
          <span className="recording-dot" />
          <div><strong>{formattedRecording} · {t("order.entry.recording")}</strong><small>{t("order.entry.recording.hint")}</small></div>
          <span className="dictation-wave" aria-hidden="true"><i /><i /><i /><i /><i /></span>
          <button type="button" onClick={() => void stopAndSummarize()}><Square size={13} /> {t("order.entry.stopSummarize")}</button>
        </div>
      )}

      {dictation === "processing" && (
        <div className="order-dictation-recording processing" role="status">
          <LoaderCircle size={17} className="spin" />
          <div><strong>{t("order.entry.summarizing")}</strong><small>{t("order.entry.summarizing.hint")}</small></div>
        </div>
      )}

      {dictation === "review" && review && (
        <section className="order-dictation-review" aria-label="Confirm dictated order summary">
          <div className="dictation-review-head">
            <div><AudioLines size={18} /><span><strong>{t("order.entry.confirmTitle")}</strong><small>{t("order.entry.confirmHint")}</small></span></div>
            <span className={`agent-result-mode ${review.mode}`}>{review.mode === "live" ? t("order.entry.live") : t("order.entry.sim")}</span>
          </div>
          <div className="dictation-source">
            <span>{t("order.entry.heard")}</span>
            <p>{review.transcript}</p>
          </div>
          <label>
            <span>{t("order.entry.editable")}</span>
            <textarea
              value={review.summary}
              onChange={(event) => setReview((current) => current ? { ...current, summary: event.target.value } : current)}
              rows={3}
            />
          </label>
          <div className="dictation-review-note"><Info size={14} /> {t("order.entry.reviewNote")}</div>
          <div className="dictation-review-actions">
            <button type="button" onClick={() => { setReview(null); setDictation("idle"); }}><X size={14} /> {t("common.cancel")}</button>
            <button type="button" onClick={startOrderDictation}><RotateCcw size={14} /> {t("order.entry.reRecord")}</button>
            <button type="button" className="confirm" onClick={() => void confirmDictation()} disabled={parsing || !review.summary.trim()}>
              {parsing ? <LoaderCircle size={14} className="spin" /> : <Check size={14} />} {t("order.entry.confirmCreate")}
            </button>
          </div>
        </section>
      )}

      <div className="omnibox-area">
        <div className="omnibox-row">
          <div className="omnibox">
            {parsing ? <LoaderCircle className="spin" size={19} /> : <Search size={19} />}
            <input
              aria-label={t("order.entry.search.aria")}
              data-testid="order-omnibox"
              value={value}
              onChange={(event) => { setValue(event.target.value); setShowCatalog(true); }}
              onFocus={() => setShowCatalog(true)}
              onBlur={() => window.setTimeout(() => setShowCatalog(false), 150)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  setShowCatalog(false);
                  void submit();
                }
                if (event.key === "Escape") setShowCatalog(false);
              }}
              placeholder={t("order.entry.search.placeholder")}
            />
            <button type="button" onClick={() => { setShowCatalog(false); void submit(); }} disabled={!value.trim() || parsing}>
              {parsing ? t("order.entry.structuring") : t("order.entry.add")}
            </button>
          </div>
          <button type="button" className="order-dictation-button" onClick={startOrderDictation} disabled={dictation === "recording" || dictation === "processing"}>
            <Mic size={15} /> {t("order.entry.dictate")}
          </button>
          <button type="button" className="smart-button" onClick={() => setValue(sample)}>
            <Sparkles size={15} /> {t("order.entry.demo")}
          </button>
          <button type="button" className="browse-button" onClick={() => insertManual("medication")}>
            {t("order.entry.browse")}
          </button>
        </div>
        {showCatalog && (isBundle || drugMatches.length > 0 || catalogMatches.length > 0) && (
          <div className="catalog-suggestions" role="listbox" aria-label="Drug and order matches">
            {isBundle && (
              <button
                type="button"
                className="catalog-suggestion bundle-add"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { setShowCatalog(false); void submit(); }}
              >
                <span className="catalog-icon"><PackagePlus size={14} /></span>
                <span className="catalog-copy"><strong>{t("order.entry.addBundle")} {bundleSegments.length} {t("toast.orders")}</strong><small>{bundleSegments.join(" · ")}</small></span>
              </button>
            )}
            {drugMatches.length > 0 && (
              <>
                <div className="catalog-suggestions-head"><Pill size={12} /> {t("order.entry.tmtHead")}</div>
                {drugMatches.map((drug) => (
                  <button
                    type="button"
                    key={drug.tmtId}
                    className="catalog-suggestion"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => addDrug(drug)}
                  >
                    <span className="catalog-icon"><Pill size={14} /></span>
                    <span className="catalog-copy"><strong>{drug.name}</strong><small>{drug.direction} · {drug.tmtId}</small></span>
                    <span className={`catalog-lane ${drug.lane}`}>{drug.lane === "continuous" ? "Continuous" : "One-Day"}</span>
                  </button>
                ))}
              </>
            )}
            {catalogMatches.length > 0 && (
              <>
                <div className="catalog-suggestions-head"><Search size={12} /> {t("order.entry.catalogHead")}</div>
                {catalogMatches.map((item) => {
                  const CatIcon = categoryIcon[item.category];
                  return (
                    <button
                      type="button"
                      key={item.name}
                      className="catalog-suggestion"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => addFromCatalog(item)}
                    >
                      <span className="catalog-icon"><CatIcon size={14} /></span>
                      <span className="catalog-copy"><strong>{item.name}</strong><small>{item.defaultDetails}</small></span>
                      <span className={`catalog-lane ${item.defaultLane}`}>{item.defaultLane === "continuous" ? "Continuous" : "One-Day"}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      <div className="quick-categories" aria-label="Quick order categories">
        <span>{t("order.entry.quickAdd")}</span>
        <button type="button" onClick={() => insertManual("medication")}><Pill size={14} /> {t("order.cat.medication")}</button>
        <button type="button" onClick={() => insertManual("lab")}><FlaskConical size={14} /> {t("order.cat.lab")}</button>
        <button type="button" onClick={() => insertManual("procedure")}><Beaker size={14} /> {t("order.cat.procedure")}</button>
        <button type="button" onClick={() => insertManual("other")}><PackagePlus size={14} /> {t("order.cat.other")}</button>
      </div>
    </div>
  );
}
